import React from "react";
import { useState } from "react";
import { Form, Button, Modal, ModalProps } from "antd";
import { validateFields } from "../../../../../config/Global";
import { assignErrorToInput } from "../../../../../store/api";
import SVGIcon from "../../../../../utils/SVGIcon";
import Config from "../../../../../config/Config";
import { createRecord } from "../../utils/slice";
import FormComponent from "./FormComponent";
import { useAppDispatch } from "../../../../../utils/hooks/hooks";

interface AddDrawerProps extends ModalProps {
  titleName: string;
  close: () => void;
}

/**
 * Renders a modal component for adding a new record in Ethnicity Management module.
 *
 * @param {AddDrawerProps} props - The props for the component.
 * @param {string} props.titleName - The title name for the modal.
 * @param {() => void} props.close - The function to close the modal.
 * @param {ModalProps} props.rest - The rest of the props for the modal.
 * @return {ReactElement} The rendered modal component for adding a new record to Ethnicity Management.
 */
const AddComponent: React.FC<AddDrawerProps> = ({
  titleName,
  close,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const drawerClose = () => {
    close();
    form.resetFields();
    setDisabled(true)
  };

// Handles the change of form values and updates the form fields accordingly.
  const validateForm = () => {
    validateFields(form, setDisabled);
  };

  /**
 * Handles the form submission by setting the saving state to true,
 * dispatching a createRecord action with the provided data,
 * and closing the drawer and resetting the form fields upon success.
 *
 * @param {any} data - The form data to be submitted.
 * @return {void} This function does not return anything.
 */
  const handleFormSubmit = (data: any) => {
    setSaving(true);

    dispatch(createRecord(data))
      .then(() => {
        drawerClose();
      })
      .catch((error: any) => {
        assignErrorToInput(form, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      title={`Add ${titleName}`}
      centered
      width={"35%"}
      onCancel={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={[
        <Button
          key="2"
          htmlType="button"
          className="cancelBtn"
          onClick={drawerClose}
        >
          Cancel
        </Button>,
        <Button
          key="1"
          disabled={disabled}
          form="addForm"
          loading={saving}
          htmlType="submit"
          type="primary"
        >
          Save
        </Button>,
      ]}
      {...rest}
    >
      <FormComponent
        form={form}
        id="addForm"
        handleSubmit={handleFormSubmit}
        onValuesChange={validateForm}
      />
    </Modal>
  );
};

export default AddComponent;
